import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Check, Trophy } from 'lucide-react';
import { SHEET_ANIMATION, BACKDROP_ANIMATION } from './sheetAnimationConfig';
/**
 * ConferenceSheet - Sport-agnostic conference/division selector
 * Can be used for NCAAF conferences, NBA divisions, etc.
 *
 * @param {boolean} isOpen - Whether sheet is open
 * @param {Function} onClose - Close handler
 * @param {Object} selectedConference - Currently selected conference
 * @param {Function} onConferenceSelect - Selection handler
 * @param {Array} conferences - Array of conference objects
 * @param {string} sport - Sport type for display customization
 */
const ConferenceSheet = ({
  isOpen,
  onClose,
  selectedConference,
  onConferenceSelect,
  conferences = [],
  sport = 'ncaaf'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Default conferences if none provided
  const defaultConferences = useMemo(() => {
    if (sport === 'ncaaf') {
      return [
        { id: 'sec', name: 'Southeastern Conference', shortName: 'SEC', tier: 'power5' },
        { id: 'big10', name: 'Big Ten Conference', shortName: 'Big Ten', tier: 'power5' },
        { id: 'acc', name: 'Atlantic Coast Conference', shortName: 'ACC', tier: 'power5' },
        { id: 'big12', name: 'Big 12 Conference', shortName: 'Big 12', tier: 'power5' },
        { id: 'pac12', name: 'Pac-12 Conference', shortName: 'Pac-12', tier: 'power5' },
        { id: 'aac', name: 'American Athletic Conference', shortName: 'AAC', tier: 'group5' },
        { id: 'mwc', name: 'Mountain West Conference', shortName: 'Mountain West', tier: 'group5' },
        { id: 'cusa', name: 'Conference USA', shortName: 'C-USA', tier: 'group5' },
        { id: 'mac', name: 'Mid-American Conference', shortName: 'MAC', tier: 'group5' },
        { id: 'sunbelt', name: 'Sun Belt Conference', shortName: 'Sun Belt', tier: 'group5' },
        { id: 'independent', name: 'Independent', shortName: 'Independent', tier: 'independent' }
      ];
    } else if (sport === 'nba') {
      return [
        { id: 'atlantic', name: 'Atlantic Division', conference: 'Eastern' },
        { id: 'central', name: 'Central Division', conference: 'Eastern' },
        { id: 'southeast', name: 'Southeast Division', conference: 'Eastern' },
        { id: 'northwest', name: 'Northwest Division', conference: 'Western' },
        { id: 'pacific', name: 'Pacific Division', conference: 'Western' },
        { id: 'southwest', name: 'Southwest Division', conference: 'Western' }
      ];
    }
    return [];
  }, [sport]);
  
  const conferencesToUse = conferences.length > 0 ? conferences : defaultConferences;
  
  // Filter conferences based on search
  const filteredConferences = useMemo(() => {
    if (!searchQuery) return conferencesToUse;
    
    const query = searchQuery.toLowerCase();
    return conferencesToUse.filter(conf => {
      const name = (conf.name || '').toLowerCase();
      const shortName = (conf.shortName || '').toLowerCase();
      const id = String(conf.id).toLowerCase();
      return name.includes(query) || shortName.includes(query) || id.includes(query);
    });
  }, [searchQuery, conferencesToUse]);
  
  // Group conferences by tier/category
  const groupedConferences = useMemo(() => {
    const groups = {};
    
    filteredConferences.forEach(conf => {
      let groupKey = 'Other';
      
      if (sport === 'ncaaf') {
        if (conf.tier === 'power5') groupKey = 'Power 5';
        else if (conf.tier === 'group5') groupKey = 'Group of 5';
        else if (conf.tier === 'independent') groupKey = 'Independent';
      } else if (sport === 'nba' && conf.conference) {
        groupKey = `${conf.conference} Conference`;
      }
      
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(conf);
    });
    
    return groups;
  }, [filteredConferences, sport]);
  
  const handleSelect = (conference) => {
    onConferenceSelect(conference);
    onClose();
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    onConferenceSelect(null);
    onClose();
    setSearchQuery('');
  };
  
  const getTierColor = (tier) => {
    if (tier === 'power5') return 'text-amber-400';
    if (tier === 'group5') return 'text-emerald-400';
    return 'text-white/60';
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            {...BACKDROP_ANIMATION}
            className="fixed inset-0 bg-black/60 z-50"
            onClick={onClose}
          />
          
          {/* Sheet */}
          <motion.div
            {...SHEET_ANIMATION}
            className="fixed bottom-0 left-0 right-0 bg-[#0a0620] rounded-t-3xl z-50 max-h-[80vh] flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white">
                  {sport === 'ncaaf' ? 'Select Conference' : 'Select Division'}
                </h3>
                <button
                  onClick={onClose}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={20} className="text-white/60" />
                </button>
              </div>
              
              {/* Search bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search ${sport === 'ncaaf' ? 'conferences' : 'divisions'}...`}
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-3 py-2 text-white placeholder-white/40 focus:outline-none focus:border-emerald-500/50"
                />
              </div>
            </div>
            
            {/* Conference list */}
            <div className="flex-1 overflow-y-auto p-4 pb-safe">
              {/* All conferences option */}
              <button
                onClick={handleClearSelection}
                className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg mb-3 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Trophy size={18} className="text-emerald-400" />
                  <span className="text-white">
                    All {sport === 'ncaaf' ? 'Conferences' : 'Divisions'}
                  </span>
                </div>
                {!selectedConference && (
                  <Check size={18} className="text-emerald-400" />
                )}
              </button>
              
              {/* Grouped conferences */}
              {Object.entries(groupedConferences).map(([groupName, groupConfs]) => (
                <div key={groupName} className="mb-4">
                  {Object.keys(groupedConferences).length > 1 && (
                    <h4 className="text-xs font-bold text-white/40 uppercase tracking-wider mb-2">
                      {groupName}
                    </h4>
                  )}
                  <div className="space-y-2">
                    {groupConfs.map(conference => (
                      <button
                        key={conference.id}
                        onClick={() => handleSelect(conference)}
                        className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col items-start">
                            <span className="text-white font-medium">
                              {conference.shortName || conference.name}
                            </span>
                            {conference.shortName && conference.name !== conference.shortName && (
                              <span className="text-xs text-white/40">
                                {conference.name}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedConference?.id === conference.id && (
                          <Check size={18} className="text-emerald-400" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              
              {filteredConferences.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-white/40">
                    No {sport === 'ncaaf' ? 'conferences' : 'divisions'} found
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConferenceSheet;